import React, { useState } from 'react';
import './food.css';

const CORRECT_PIN = process.env.REACT_APP_FOOD_LOG_PIN;

export default function MapUpload() {
  const [screen, setScreen] = useState('pin');
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState('');
  const [location, setLocation] = useState('');
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [error, setError] = useState('');
  const [results, setResults] = useState(null); // { uploaded, failed, geocoded }

  function handlePin(e) {
    e.preventDefault();
    if (pin === CORRECT_PIN) {
      setScreen('upload');
    } else {
      setPinError('Wrong PIN');
    }
  }

  function handleFileSelect(e) {
    const list = Array.from(e.target.files || []);
    setFiles(list);
    setPreviews(list.map((f) => URL.createObjectURL(f)));
    setError('');
  }

  async function handleUpload() {
    if (!files.length || !location.trim() || uploading) return;
    setUploading(true);
    setError('');
    setProgress({ done: 0, total: files.length });

    try {
      const signRes = await fetch('/api/photos-upload-sign', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin, location: location.trim() }),
      });
      const signData = await signRes.json();
      if (!signRes.ok) throw new Error(signData.error || 'Could not sign upload');

      const uploaded = [];
      const failed = [];
      for (const file of files) {
        const form = new FormData();
        form.append('file', file);
        form.append('api_key', signData.apiKey);
        form.append('timestamp', signData.timestamp);
        form.append('signature', signData.signature);
        form.append('folder', signData.folder);
        form.append('context', signData.context);
        try {
          const r = await fetch(`https://api.cloudinary.com/v1_1/${signData.cloudName}/image/upload`, {
            method: 'POST',
            body: form,
          });
          const data = await r.json();
          if (!r.ok) throw new Error((data.error && data.error.message) || 'Upload failed');
          uploaded.push({ name: file.name, thumb: data.secure_url });
        } catch (err) {
          failed.push({ name: file.name, error: err.message });
        }
        setProgress((p) => ({ ...p, done: p.done + 1 }));
      }

      setResults({ uploaded, failed, geocoded: signData.geocoded, location: location.trim() });
      setScreen('success');
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  }

  function reset() {
    setFiles([]);
    setPreviews([]);
    setLocation('');
    setResults(null);
    setScreen('upload');
  }

  if (screen === 'pin') {
    return (
      <div className="fl-page">
        <h1 className="fl-title">Map Upload</h1>
        <form className="fl-form" onSubmit={handlePin}>
          <input
            className="fl-pin-input"
            type="password"
            inputMode="numeric"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            placeholder="Enter PIN"
            autoFocus
          />
          {pinError && <p className="fl-error">{pinError}</p>}
          <button className="fl-btn" type="submit">Continue</button>
        </form>
        <a className="fl-back" href="/photos">← Back to map</a>
      </div>
    );
  }

  if (screen === 'upload') {
    return (
      <div className="fl-page fl-page-wide">
        <h1 className="fl-title">Map Upload</h1>
        <input
          className="fl-date-input"
          type="text"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          placeholder="Location — e.g. Rome, Italy"
        />
        <label className="fl-photo-area fl-photo-area-multi">
          {previews.length ? (
            <div className="fl-multi-grid">
              {previews.map((src, i) => <img key={i} className="fl-multi-thumb" src={src} alt="" />)}
            </div>
          ) : (
            <span className="fl-photo-hint">Tap to select photos</span>
          )}
          <input type="file" accept="image/*" multiple onChange={handleFileSelect} className="fl-file-input" />
        </label>
        {error && <p className="fl-error">{error}</p>}
        {files.length > 0 && (
          <button className="fl-btn" onClick={handleUpload} disabled={uploading || !location.trim()}>
            {uploading ? `Uploading ${progress.done}/${progress.total}…` : `Upload ${files.length} photo${files.length === 1 ? '' : 's'}`}
          </button>
        )}
        <a className="fl-back" href="/photos">← Back to map</a>
      </div>
    );
  }

  const { uploaded, failed, geocoded, location: loggedLocation } = results;
  return (
    <div className="fl-page fl-page-wide">
      <div className="fl-success-mark">✓</div>
      <p className="fl-success-date">{uploaded.length} photo{uploaded.length === 1 ? '' : 's'} uploaded</p>
      <p className="fl-success-label">
        {loggedLocation} · {geocoded.lat.toFixed(3)}, {geocoded.lng.toFixed(3)}
      </p>
      {uploaded.length > 0 && (
        <div className="fl-multi-grid">
          {uploaded.map((u, i) => <img key={i} className="fl-multi-thumb" src={u.thumb} alt="" />)}
        </div>
      )}
      {failed.length > 0 && (
        <p className="fl-error">{failed.length} failed: {failed.map((f) => f.name).join(', ')}</p>
      )}
      <button className="fl-btn" onClick={reset}>Upload more</button>
      <a className="fl-back" href="/photos">← Back to map</a>
    </div>
  );
}
